import React, { FC } from 'react';

import { Box } from '@rover/kibble/core';
import RoverLoading from '@rover/react-lib/src/components/utils/RoverLoading/RoverLoading';
import getQueryError from '@rover/react-lib/src/utils/getQueryError';
import {
  FLAGS,
  useFrontendConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import type PageComponent from '@rover/types/src/PageComponent';

type Flag = (typeof FLAGS)[keyof typeof FLAGS];

export interface RolloutGuardProps {
  flag: Flag;
  onIneligible: () => JSX.Element | null;
  loadingComponent?: React.ComponentType;
  children: JSX.Element;
}

const RolloutGuard: FC<RolloutGuardProps> = ({
  flag,
  onIneligible,
  loadingComponent: LoadingComponent,
  children,
}) => {
  const { isFlagActive, isLoading, error: frontendConfigurationError } = useFrontendConfiguration();

  if (frontendConfigurationError) {
    throw getQueryError(frontendConfigurationError);
  }

  if (isLoading) {
    return LoadingComponent ? (
      <LoadingComponent />
    ) : (
      <RoverLoading isLoading data-testid="full-page-loading">
        <Box minHeight="100vh" />
      </RoverLoading>
    );
  }

  if (!isFlagActive(flag)) {
    return onIneligible();
  }

  return children;
};

function withRolloutGuard<TProps extends object>(
  Component: PageComponent<TProps>,
  flag: Flag,
  onIneligible: (props: TProps) => JSX.Element | null
): React.ComponentType<TProps> {
  function RolloutGuardWrapper(props: TProps): JSX.Element {
    return (
      <RolloutGuard flag={flag} onIneligible={() => onIneligible(props)}>
        <Component {...props} />
      </RolloutGuard>
    );
  }

  RolloutGuardWrapper.reactQueryPrefetch = Component.reactQueryPrefetch;
  RolloutGuardWrapper.shouldSetBackHistoryHeader = Component.shouldSetBackHistoryHeader;
  RolloutGuardWrapper.shouldSetBackUrlHeader = Component.shouldSetBackUrlHeader;

  return RolloutGuardWrapper;
}

export default withRolloutGuard;
